import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function FutureValueCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "Future Value Calculator", url: "https://calclab.app/calculator/future-value" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Future Value Calculator",
      "Calculate future value of investment using FV = PV × (1 + r)^n. Free online finance calculator.",
      "https://calclab.app/calculator/future-value",
      lang
    )
  ]);
  const [presentValue, setPresentValue] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [periods, setPeriods] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const PV = parseFloat(presentValue);
    const r = parseFloat(interestRate) / 100;
    const n = parseFloat(periods);

    if (isNaN(PV) || isNaN(r) || isNaN(n) || PV <= 0 || r < 0 || n <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const FV = PV * Math.pow(1 + r, n);
    setResult(FV);
    toast.success("Future value calculated!");
  };

  const reset = () => {
    setPresentValue("");
    setInterestRate("");
    setPeriods("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Future Value Calculator</CardTitle>
          <CardDescription>
            Calculate future value of investment: FV = PV × (1 + r)^n
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="presentValue">Present Value ($)</Label>
            <Input
              id="presentValue"
              type="number"
              placeholder="10000"
              value={presentValue}
              onChange={(e) => setPresentValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="interestRate">Interest Rate (% per period)</Label>
            <Input
              id="interestRate"
              type="number"
              placeholder="6"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="periods">Number of Periods</Label>
            <Input
              id="periods"
              type="number"
              placeholder="10"
              value={periods}
              onChange={(e) => setPeriods(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg break-all overflow-wrap-anywhere">
                    <strong>Future Value:</strong> ${result.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.toFixed(2)} />
                </div>
              </div>
              
              <CalculatorSuperCalcCTA config={calculatorCTAs["future-value"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
